/**
 * Route an image URL through the `cdn-cgi.getcardbase.com/cbm` proxy,
 * mirroring the web client's `compressedImageUrl`
 * (web repo: `src/utils/imageProcessing/index.js`).
 *
 * Activation: CDN is applied only when `VITE_CDN_URL` is set (usually in
 * production). In dev the env var is empty, so this util is a passthrough
 * — local Rails / non‑allowlisted origins render as‑is.
 *
 * Passthrough cases (even when CDN is enabled):
 *   - null / non‑string URL → `null`
 *   - URL already points at the CDN origin
 *   - `data:` / `blob:` URLs
 *   - `http://` URLs (CDN only fetches https origins)
 *
 * Transformations:
 *   - `width` + `height` → `/rs:fit:W*m:H*m:1` resize (multiplier default 2.6)
 *   - `crop` → `/crop:<x:y:w:h>`
 *   - `quality` → `/q:<Q>` (default 90)
 */

const CDN_ORIGIN = (import.meta.env.VITE_CDN_URL ?? '').replace(/\/+$/, '');

export interface CompressImageOptions {
  url: string | null | undefined;
  width?: number;
  height?: number;
  quality?: number;
  multiplier?: number;
  crop?: string;
}

export function compressedImageUrl({
  url,
  width,
  height,
  quality = 90,
  multiplier = 2.6,
  crop,
}: CompressImageOptions): string | null {
  if (!url || typeof url !== 'string') return null;
  if (!CDN_ORIGIN) return url;
  if (url.startsWith(CDN_ORIGIN)) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (!url.startsWith('https://')) return url;

  let resizePart = '';
  if (width && height) {
    resizePart = `/rs:fit:${Math.round(width * multiplier)}:${Math.round(height * multiplier)}:1`;
  }

  const cropPart = crop ? `/crop:${crop}` : '';

  return `${CDN_ORIGIN}/cbm${cropPart}${resizePart}/q:${quality}/plain/${normalizeSrc(url)}`;
}

function normalizeSrc(src: string): string {
  return src.startsWith('/') ? src.slice(1) : src;
}
